import { Box, Text, TextInput, Textarea, NumberInput, PasswordInput, Button, Rating as MantineRating } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { isEmptyRichText, renderRichTextWithVars, renderTextWithVars, VAR_BUBBLE_CSS } from '@/features/editor/utils/renderRichText';
import { NAVY, inputStyles } from './styles';
import { YesNoElement } from './YesNoElement';
import { MultiChoiceElement } from './MultiChoiceElement';
import { ImageUploadElement } from './ImageUploadElement';
import { isFullWidthButton, placeButton } from '@/features/editor/components/PageElements/buttonAlign';

export interface PageElementRendererProps {
  element: any;
  value: any;
  onChange: (value: any) => void;
  onBranchSelect: (targetPageId: string, value: any) => void;
  onAdvance: () => void;
  sessionState?: Record<string, any>;
  sessionId?: string | null;
  mode?: 'preview' | 'live';
  /** Set when the viewer tried to leave the page with this required field empty. */
  error?: string;
}

// Mantine inputs show `error` (and the asterisk) themselves; the rest get a line under them.
const SHOWS_OWN_ERROR = new Set(['ShortText', 'Multiline', 'Password', 'Date', 'Number']);

export function PageElementRenderer(props: PageElementRendererProps) {
  const { element, error } = props;
  const rendered = renderElement(props);
  if (!error || SHOWS_OWN_ERROR.has(element.type)) return rendered;
  return (
    <Box>
      {rendered}
      <Text size="xs" c="red.7" mt={4} role="alert">
        {error}
      </Text>
    </Box>
  );
}

function renderElement({ element, value, onChange, onBranchSelect, onAdvance, sessionState = {}, sessionId, mode, error }: PageElementRendererProps) {
  const text = renderTextWithVars(element.label || '', sessionState);
  // Inputs mark themselves via withAsterisk; custom labels carry the mark here.
  const required = element.required === true;
  const label = required && !SHOWS_OWN_ERROR.has(element.type) ? (
    <>
      {text} <span style={{ color: 'var(--mantine-color-red-7)' }}>*</span>
    </>
  ) : (
    text
  );

  switch (element.type) {
    case 'ShortText':
      return (
        <TextInput
          label={label}
          withAsterisk={required}
          error={error}
          placeholder={element.placeholder || 'Enter text'}
          value={value || ''}
          onChange={(e) => onChange(e.currentTarget.value)}
          styles={inputStyles}
          radius="md"
        />
      );

    case 'Multiline':
      return (
        <Textarea
          label={label}
          withAsterisk={required}
          error={error}
          placeholder={element.placeholder || 'Enter text'}
          minRows={element.rows || 4}
          value={value || ''}
          onChange={(e) => onChange(e.currentTarget.value)}
          styles={inputStyles}
          radius="md"
        />
      );

    // Password had no case here and fell through to the placeholder box, so a
    // viewer could never answer it (and a required one would block the page).
    case 'Password':
      return (
        <PasswordInput
          label={label}
          withAsterisk={required}
          error={error}
          placeholder={element.placeholder || 'Enter password'}
          value={value || ''}
          onChange={(e) => onChange(e.currentTarget.value)}
          styles={inputStyles}
          radius="md"
        />
      );

    case 'Date':
      return (
        <DateInput
          label={label}
          withAsterisk={required}
          error={error}
          placeholder="Pick a date"
          value={value ? new Date(value) : null}
          onChange={(date) => onChange(date?.toISOString())}
          styles={inputStyles}
          radius="md"
        />
      );

    case 'Number':
      return (
        <NumberInput
          label={label}
          withAsterisk={required}
          error={error}
          placeholder="Enter number"
          min={element.min}
          max={element.max}
          value={value || ''}
          onChange={(val) => onChange(val)}
          styles={inputStyles}
          radius="md"
        />
      );

    case 'YesNo':
      return (
        <YesNoElement
          element={element}
          value={value}
          label={label}
          onChange={onChange}
          onBranchSelect={onBranchSelect}
        />
      );

    case 'MultiChoice':
      return (
        <MultiChoiceElement
          element={element}
          value={value}
          label={label}
          onChange={onChange}
          onBranchSelect={onBranchSelect}
        />
      );

    case 'ImageUpload':
      return (
        <ImageUploadElement
          element={element}
          label={text}
          value={value}
          onChange={onChange}
          sessionId={sessionId}
          mode={mode}
        />
      );

    case 'Rating':
      return (
        <Box>
          <Text size="sm" fw={500} mb="xs" c="#2A3140">{label}</Text>
          <MantineRating
            count={element.maxRating || 5}
            value={value || 0}
            onChange={onChange}
            color={NAVY}
          />
        </Box>
      );

    case 'Button':
      return placeButton(element,
        <Button
          variant={element.buttonStyle === 'outline' ? 'outline' : element.buttonStyle === 'secondary' ? 'light' : 'filled'}
          color={NAVY}
          fullWidth={isFullWidthButton(element)}
          radius="md"
          size="md"
          onClick={() => {
            if (element.targetPageId) {
              // Navigate to specific target page
              onBranchSelect(element.targetPageId, 'clicked');
            } else {
              // Just advance to next node in flow
              onChange('clicked');
              onAdvance();
            }
          }}
        >
          {element.buttonText || 'Button'}
        </Button>
      );

    case 'RichText':
      if (isEmptyRichText(element.content)) return null;

      return (
        <Box>
          <style>{VAR_BUBBLE_CSS}</style>
          <div className="rich-text-render" dangerouslySetInnerHTML={{ __html: renderRichTextWithVars(element.content, sessionState) }} />
        </Box>
      );

    default:
      return (
        <Box p="xs" style={{ border: '1px dashed #ccc', borderRadius: 4 }}>
          <Text size="sm" c="dimmed">{element.type}: {label}</Text>
        </Box>
      );
  }
}
